// QnADataStructureAndAlgorithm.jsx
import React, { useMemo, useState } from "react";
import questions from "./qnaJSONdata/QnADataStructureAndAlgorithm.json";
import QnASideBarRight from "./QnASideBarRight";
import ScrollButton from "../../ScrollButton";

/**
 * QnADataStructureAndAlgorithm — inline Q&A with LEFT TOC + right sidebar + keyword filter
 * - Adds Download PDF (top-right + left sidebar) and Print button
 * - Accessible Bootstrap 5 layout
 */

const DSA_PDF = "/pdfs/data-structures-algorithms-interview-questions.pdf"; // ensure file exists in /public/pdfs/
const normalize = (s = "") => s.toLowerCase();

/* ===== UI bits ===== */
function FilterBar({ value, onChange, total, shown }) {
  return (
    <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
      <input
        className="form-control form-control-sm"
        style={{ maxWidth: 420 }}
        type="search"
        placeholder="Filter (e.g., arrays, linked lists, trees, graphs, DP, complexity)…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="Filter questions"
      />
      <span className="text-muted small">Showing {shown} of {total}</span>
    </div>
  );
}

function QAInlineList({ items }) {
  if (!items.length) {
    return (
      <div className="text-center text-muted py-5">
        No questions match your filter. Try another keyword.
      </div>
    );
  }

  return (
    <ol className="ps-3">
      {items.map((it, idx) => (
        <li key={it.id ?? idx} className="mb-4">
          <h5 className="mb-2">{it.question}</h5>
          <div className="text-body" style={{ whiteSpace: "pre-wrap" }}>{it.answer}</div>
          {it.code ? (
            <pre className="mt-2 mb-0 p-3 bg-light rounded border" style={{ overflow: "auto" }}>
              <code>{it.code}</code>
            </pre>
          ) : null}
        </li>
      ))}
    </ol>
  );
}

function OnThisPageLeft({ pdfHref }) {
  return (
    <div className="card shadow-sm border-0">
      <div className="card-body">
        <h2 className="h6 text-uppercase text-muted mb-2">On this page</h2>
        <nav className="nav flex-column small" aria-label="Section links">
          <a className="nav-link px-0" href="#dsa">Data Structures &amp; Algorithms Q&amp;A</a>
          <hr className="my-2" />
          <a
            className="nav-link px-0 text-primary d-inline-flex align-items-center"
            href={pdfHref}
            download
            aria-label="Download Data Structures & Algorithms interview questions PDF"
          >
            <span className="me-2" aria-hidden="true">⬇︎</span>
            Download PDF
          </a>
        </nav>
      </div>
    </div>
  );
}

function TopActions({ pdfHref }) {
  return (
    <div className="d-flex gap-2">
      <a
        href={pdfHref}
        download
        className="btn btn-outline-danger btn-sm"
        aria-label="Download Data Structures & Algorithms interview questions as PDF"
      >
        Download PDF
      </a>
      <button
        type="button"
        className="btn btn-outline-secondary btn-sm"
        onClick={() => window.print()}
        aria-label="Print this page"
      >
        Print
      </button>
    </div>
  );
}

/* ===== Page ===== */
export default function QnADataStructureAndAlgorithm() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const qn = normalize(query);
    if (!qn) return questions;
    return questions.filter((x) =>
      normalize(`${x.question} ${x.answer ?? ""} ${x.code ?? ""}`).includes(qn)
    );
  }, [query]);

  return (
    <div className="container-fluid">
      <div className="row g-4 flex-xl-nowrap">
        {/* LEFT: On This Page (sticky) */}
        <aside className="d-none d-xl-block col-xl-2 pt-3">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <OnThisPageLeft pdfHref={DSA_PDF} />
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7 pt-3" role="main">
          <header className="mb-2 d-flex justify-content-between align-items-start">
            <div>
              <h1 className="h2 mb-1">Data Structures &amp; Algorithms Interview Questions</h1>
              <p className="text-muted mb-0">
                Use the filter to quickly find topics like arrays, linked lists, stacks/queues, trees/tries,
                graphs, heaps, hashing, sorting/searching, DP/greedy, and complexity analysis.
              </p>
            </div>
            <TopActions pdfHref={DSA_PDF} />
          </header>

          <FilterBar value={query} onChange={setQuery} total={questions.length} shown={filtered.length} />

          <section id="dsa" className="pt-2">
            <QAInlineList items={filtered} />
          </section>

          <footer className="border-top mt-4 pt-3 small text-muted">
            Tip: State your approach, time/space complexity, and edge cases (empty input, duplicates, sorted/unsorted,
            negative numbers, Unicode). If time remains, compare alternative strategies.
          </footer>

          <ScrollButton />
        </main>

        {/* RIGHT: existing sidebar */}
        <aside className="col-12 col-lg-4 col-xl-3 pt-3 border-start">
          <QnASideBarRight />
        </aside>
      </div>
    </div>
  );
}
